import React from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { Download, Trash } from "react-bootstrap-icons";
import { format } from "date-fns";

import {
  deleteDocument,
  downloadDocument,
  getDocuments,
  uploadDocument,
} from "../../../../../api/Document";

export default function DocumentsDetail(props: {
  bagObjectId: string;
  onCountChange?: (count: number) => void;
}): React.JSX.Element {
  /**
   * Lists the documents of a BAG object, with upload, download and delete.
   * Reports the document count via onCountChange (e.g. for the tab title).
   */
  const [documents, setDocuments] = React.useState<IDocument[]>([]);
  const [fileInputKey, setFileInputKey] = React.useState<number>(0);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { onCountChange } = props;
  const refreshDocuments = React.useCallback(async () => {
    await getDocuments(props.bagObjectId)
      .then((retrievedDocuments: IDocument[]) => {
        setDocuments(retrievedDocuments);
        onCountChange?.(retrievedDocuments.length);
      })
      .catch((error: IApiError) => setErrorMessage(error.message));
  }, [props.bagObjectId, onCountChange]);

  React.useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  const uploadErrorMessage = (error: IApiError): string => {
    /**
     * Prefer the backend's (Dutch) file validation message over the generic one.
     */
    const fileErrors = (error.data as { file?: string[] } | undefined)?.file;
    return fileErrors?.length ? fileErrors.join(" ") : error.message;
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    /**
     * Upload immediately when a file is chosen in the file explorer.
     */
    const files = e.target.files;
    const file = files?.length ? files[0] : null;
    if (!file) {
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    await uploadDocument(props.bagObjectId, file)
      .then(async () => {
        setSuccessMessage("Document is geüpload.");
        await refreshDocuments();
      })
      .catch((error: IApiError) => setErrorMessage(uploadErrorMessage(error)));
    // Remount the input so choosing the same file again re-triggers onChange.
    setFileInputKey((key) => key + 1);
    setIsLoading(false);
  };

  const handleDownload = async (document_: IDocument) => {
    setErrorMessage("");
    await downloadDocument(props.bagObjectId, document_).catch(
      (error: IApiError) => setErrorMessage(error.message),
    );
  };

  const handleDelete = async (document_: IDocument) => {
    if (
      !window.confirm(
        `Weet je zeker dat je '${document_.originalFilename}' wilt verwijderen?`,
      )
    ) {
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    await deleteDocument(props.bagObjectId, document_.id)
      .then(async () => {
        setSuccessMessage("Document is verwijderd.");
        await refreshDocuments();
      })
      .catch((error: IApiError) => setErrorMessage(error.message));
  };

  return (
    <div>
      {documents.length === 0 ? (
        <p>Nog geen documenten.</p>
      ) : (
        <Table size="sm" striped={true} style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th>Bestandsnaam</th>
              <th style={{ width: "9.5rem" }}>Geüpload op</th>
              <th style={{ width: "22%" }}>Door</th>
              <th style={{ width: "6.5rem" }}></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document_) => (
              <tr key={document_.id}>
                <td
                  className="text-truncate"
                  title={document_.originalFilename}
                >
                  {document_.originalFilename}
                </td>
                <td>{format(document_.created, "dd-MM-yyyy HH:mm")}</td>
                <td
                  className="text-truncate"
                  title={document_.uploadedBy || undefined}
                >
                  {document_.uploadedBy || "-"}
                </td>
                <td className="text-end text-nowrap">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    title="Downloaden"
                    aria-label="Downloaden"
                    onClick={() => handleDownload(document_)}
                  >
                    <Download />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    title="Verwijderen"
                    aria-label="Verwijderen"
                    onClick={() => handleDelete(document_)}
                  >
                    <Trash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="mt-3">
        <Form.Control
          key={fileInputKey}
          ref={fileInputRef}
          className="visually-hidden"
          size="sm"
          type="file"
          id="document-file"
          aria-label="Document uploaden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          onChange={handleFileChosen}
        />
        <Button
          size="sm"
          title="Bestand uploaden"
          variant="primary"
          className="px-4"
          disabled={isLoading}
          style={{ minWidth: "10rem" }}
          onClick={() => fileInputRef.current?.click()}
        >
          {isLoading ? (
            <Spinner animation="border" variant="dark" size="sm" />
          ) : (
            "Bestand uploaden"
          )}
        </Button>
      </div>

      {errorMessage ? (
        <Form.Text className="text-danger mb-1">{errorMessage}</Form.Text>
      ) : (
        successMessage && (
          <Form.Text className="text-success mb-1">{successMessage}</Form.Text>
        )
      )}
    </div>
  );
}
